<template>
	<v-form
		ref="form"
		v-model="valid"
		class="login-form"
		lazy-validation
		@submit.prevent="onSubmit"
	>
		<div class="post-text__wrapper">
			<div class="create-thread-modal__scroll-wrapper">
				<div class="d-flex flex-column">
					<div class="post-text__group-container mt-3 pr-1">
						<span class="post-text__group-container__title"
							>Community
							<span class="post-text__group-container__title__required">*</span></span
						>

						<v-skeleton-loader
							v-if="isLoading && isLocalLoading"
							class="post-text__category-loader"
							type="card"
						/>

						<div v-else class="post-text__category-select-wrapper">
							<v-select
								ref="categorySelect"
								v-model="selectedCategory"
								:items="categoryOptions"
								item-text="category_name"
								item-value="thread_category_id"
								:menu-props="categoryMenuProps"
								:rules="rules.required"
								:disabled="isCategoryDisabled"
								append-icon="mdi-chevron-right"
								return-object
								solo
								hide-details
								class="post-text__category-select"
								@change="onSelectChange"
								@click="onOpenCategorySelect"
							/>
						</div>
					</div>
					<div class="carousel-wrapper mt-3">
						<div class="d-flex justify-space-between">
							<span class="title-video">Featured Images</span>
						</div>
						<div
							v-if="slider_images.length === 0"
							class="carousel-wrapper__file-container mt-3"
						>
							<v-icon color="#F3F3F6" :size="50">mdi-image</v-icon>
							<div class="carousel-wrapper__file-container-title">
								<div class="d-flex">Drag and Drop</div>
							</div>
							<div class="carousel-wrapper__file-container-description">
								JPG, PNG. Max 30 MB each
							</div>
						</div>
						<div v-else class="carousel mt-3">
							<div class="carousel-track" :style="trackStyle">
								<div
									v-for="(image, index) in slider_images"
									:key="image.id"
									class="carousel-item"
									:class="{
										active: currentIndex === index,
										single: slider_images.length === 1,
									}"
								>
									<img :src="image.image_url" alt="Uploaded Image" />
									<button
										type="button"
										class="remove-btn"
										@click="removeImages(index)"
									>
										<Icon type="close" :size="24" color="var(--accent-color)" />
									</button>
								</div>
							</div>
						</div>
						<div class="indicators">
							<span
								v-if="slider_images.length > 1"
								v-for="(image, index) in slider_images"
								:key="'indicator-' + image.id"
								:class="{ active: currentIndex === index }"
								@click="goToSlide(index)"
							></span>
						</div>
						<div
							:style="{
								color:
									slider_images.length >= 10
										? '#9b9b9b'
										: 'var(--text_button_tint)',
							}"
							@click="triggerPhotoInput"
							class="carousel-button u-cursor-pointer"
						>
							+ Add Images
							<input
								ref="photoInput"
								id="fileInput"
								type="file"
								accept="image/*"
								multiple
								@change="handleFileUpload"
								:disabled="slider_images.length >= 10"
								style="display: none"
							/>
						</div>
					</div>
					<div class="post-text__body-contaner mt-3">
						<span class="post-text__group-container__title"
							>Title
							<span class="post-text__group-container__title__required">*</span>
						</span>
						<textarea
							class="post-text__group-container__title__input-title"
							placeholder="What's your discussion about?"
							v-model="fields.thread_title"
							rows="1"
							@input="autoResize"
							ref="titleTextarea"
						/>
					</div>
					<div class="post-text__body-contaner mt-3">
						<span class="post-text__group-container__title"
							>Body <span class="post-text__group-container__title__required">*</span>
						</span>
						<RichTextEditor
							v-model="fields.thread_text"
							placeholder="What would you like to discuss?"
						/>
					</div>
				</div>
				<div class="attachments">
					<div class="d-flex justify-space-between">
						<h3 class="title-video">Attachments</h3>
						<div class="d-flex">
							<div>
								<Icon type="up" :size="24" color="#9b9b9b" />
							</div>
						</div>
					</div>
					<div class="attachments__attachments-list">
						<div>
							<div
								v-for="(attachment, index) in this.uploads"
								:key="attachment.upload_id"
								class="attachment-item d-flex justify-space-between"
							>
								<div class="d-flex align-center">
									<PostAttachments
										backgroundColor="#F3F3F6"
										:attachment="attachment"
										style="margin-top: 5px"
									/>
									<span>{{ attachment.filename }}</span>
								</div>
								<button type="button" @click="removeFile(index)" class="delete-btn">
									<Icon type="delete" :size="24" color="#9B9B9B" />
								</button>
							</div>
						</div>
					</div>
					<div class="add-attachment">
						<button
							type="button"
							@click="triggerFileInput"
							class="attachments__add-btn"
						>
							+ Add Attachments
						</button>
						<input
							type="file"
							ref="fileAttachmentInput"
							@change="handleUploadFile"
							multiple
							style="display: none"
						/>
					</div>
				</div>
			</div>
		</div>

		<div class="post-text__btn-container mt-3">
			<button class="post-text__btn-container__cancel" type="button" @click="closeModal">
				Cancel
			</button>

			<button
				class="post-text__btn-container__submit"
				type="button"
				:disabled="loading"
				@click="onSubmit"
				:style="{ backgroundColor: selectedCategory ? 'var(--primary_color)' : '#9b9b9b' }"
			>
				{{ edit ? 'Save' : 'Publish' }}
			</button>
		</div>

		<p
			v-for="error in postErrors"
			:key="error.message"
			class="red--text mt-2 mb-0"
			style="width: 100%"
		>
			{{ error.message }}
		</p>
	</v-form>
</template>

<script>
import { ruleRequired } from '@/utils/rules'
import Form from '@/mixins/Form'
import { mapActions, mapGetters } from 'vuex'
import RichTextEditor from '@/components/RichTextEditor.vue'
import PostAttachments from '@/components/Post/PostAttachments.vue'
import Dwh from '@/mixins/Dwh'

export default {
	name: 'PostTextForm',
	components: { PostAttachments, RichTextEditor },

	mixins: [Form, Dwh],

	props: {
		activeTab: {
			type: Number,
			default: 0,
		},
		isEmptyCategories: {
			type: Boolean,
			default: false,
		},
		categoryId: {
			type: Number,
			default: null,
		},
		showForm: {
			type: Boolean,
			default: false,
		},
		edit: {
			type: Boolean,
			default: false,
		},
		editableForum: {
			type: Object,
			default: () => ({}),
		},
	},

	data: () => ({
		selectedCategory: null,
		slider_images: [],
		currentIndex: 0,
		initialSnapshot: null,
		isCategorySearching: false,
		fields: {
			publish_timestamp: Math.floor(Date.now() / 1000),
			thread_category_id: null,
			image_upload_ids: [],
			thread_text: '',
			upload_ids: [],
			thread_title: '',
			thread_type: 2,
		},
		uploads: [],
		moreFiles: false,
		page: 1,
		loadingMore: false,
		showConfirmDeleteImageButtons: false,

		categoryMenuProps: {
			offsetY: true,
			minWidth: 250,
			maxWidth: 250,
			contentClass: 'post-text-category-menu',
		},
	}),

	computed: {
		...mapGetters('settings', ['chaptersList', 'appCore', 'styles']),

		...mapGetters('forum', [
			'forumForRoute',
			'forumCategories',
			'chosenCategory',
			'isLoading',
			'forumBeingEdited',
			'modalCategories',
			'isLocalLoading',
			'pageModalCategories',
		]),

		...mapGetters('post', ['isFileLoading', 'postFile', 'postErrors']),
		...mapGetters('tag', ['tags']),
		trackStyle() {
			if (this.slider_images.length <= 1) {
				return { transform: 'translateX(0)' }
			}

			const slideWidth = this.$el?.querySelector('.carousel-item')?.offsetWidth || 0

			const gap = 8

			const shift = this.currentIndex * (slideWidth + gap)

			return {
				transform: `translateX(-${shift}px)`,
			}
		},

		categoryOptions() {
			if (this.edit && this.editableForum?.category) {
				return this.modalCategories?.length
					? this.modalCategories
					: [this.editableForum.category]
			}

			return this.modalCategories
		},

		rules() {
			return {
				required: [(v) => ruleRequired(v) || 'Field is required.'],
			}
		},

		section() {
			if (!this.appCore?.app_menu) return null

			const parsedMenu = JSON.parse(this.appCore.app_menu)

			return parsedMenu.find((item) => item.url.includes('threads'))
		},

		isCategoryDisabled() {
			return !!this.categoryId && this.edit
		},
	},

	watch: {
		modalCategories: {
			handler(categories) {
				if (!categories?.length) return
				if (this.selectedCategory) return

				if (this.categoryId) {
					const current = categories.find((c) => c.thread_category_id === this.categoryId)
					if (!current && categories.length === 1 && !this.chosenCategory) {
						this.selectedCategory = categories[0]
						this.fields.thread_category_id = categories[0].thread_category_id
						this.markInitialSnapshot()
						return
					}
					if (current) {
						this.selectedCategory = current
						this.fields.thread_category_id = current.thread_category_id
						this.markInitialSnapshot()
					} else {
						this.ensureCategorySelected()
					}
					return
				}

				if (categories.length === 1 && !this.chosenCategory) {
					this.selectedCategory = categories[0]
					this.fields.thread_category_id = categories[0].thread_category_id
					this.markInitialSnapshot()
				}
			},
			immediate: true,
		},

		editableForum: {
			immediate: true,
			deep: true,
			handler() {
				if (!this.edit || !this.editableForum?.thread_id) return

				this.initForm()
				this.resizeTitleTextarea()

				this.$nextTick(() => {
					setTimeout(() => {
						this.markAsSaved()
					}, 0)
				})
			},
		},
		showForm(val) {
			if (!val) return

			this.$nextTick(() => {
				this.attachScrollListener()
			})
		},

		chosenCategory: {
			handler(category) {
				if (this.edit) return
				if (this.categoryId) return
				if (this.selectedCategory && !category) return
				this.selectedCategory = category || null
				this.fields.thread_category_id = category?.thread_category_id || 0
				this.markInitialSnapshot()
			},
			immediate: true,
		},
	},

	beforeDestroy() {
		this.removeScrollListener()
	},

	methods: {
		...mapActions('post', ['uploadFile']),

		...mapActions('forum', [
			'addPost',
			'editForum',
			'getModalCategories',
			'resetModalCategories',
		]),
		...mapActions('tag', ['fetchTags']),
		...mapActions('auth', ['showAlert']),
		resizeTitleTextarea() {
			this.$nextTick(() => {
				const el = this.$refs.titleTextarea
				if (!el) return

				el.style.height = 'auto'
				el.style.height = `${el.scrollHeight}px`
			})
		},
		markInitialSnapshot() {
			if (this.initialSnapshot) return

			this.$nextTick(() => {
				this.markAsSaved()
			})
		},
		async ensureCategorySelected() {
			if (!this.categoryId) return
			if (this.selectedCategory) return
			if (this.isCategorySearching) return

			this.isCategorySearching = true

			try {
				const found = this.modalCategories?.find(
					(c) => c.thread_category_id === this.categoryId
				)

				if (found) {
					this.selectedCategory = found
					this.fields.thread_category_id = found.thread_category_id
					this.markInitialSnapshot()
					return
				}

				let page = 2

				while (true) {
					if (page > this.pageModalCategories) break

					await this.getModalCategories({
						query: '?category_type=1&filter=bookmark&fields=thread_category_id,category_name',
						page,
					})

					const foundOnPage = this.modalCategories?.find(
						(c) => c.thread_category_id === this.categoryId
					)

					if (foundOnPage) {
						this.selectedCategory = foundOnPage
						this.fields.thread_category_id = foundOnPage.thread_category_id
						this.markInitialSnapshot()
						return
					}

					page++
				}
			} finally {
				this.isCategorySearching = false
			}
		},
		autoResize(event) {
			const el = event.target
			el.style.height = 'auto'
			el.style.height = el.scrollHeight + 'px'
		},
		dwhClickCancelCreate() {
			this.sendTrigger({
				category: 'Create Discussion',
				action: 'click_cancel_create_discussion',
				module: 'Forums / Communities',
				title: 'Create Discussion',
			})
		},
		dwhClickAddAttachment(count) {
			this.sendTrigger({
				category: 'Create Discussion',
				action: 'click_add_file_discussion',
				module: 'Forums / Communities',
				title: 'Create Discussion',
				data: count,
			})
		},
		dwhClickAddPhoto(count) {
			this.sendTrigger({
				category: 'Create Discussion',
				action: 'click_add_photo_discussion',
				module: 'Forums / Communities',
				title: 'Create Discussion',
				data: count,
			})
		},
		dwhClickSelectCommunity(selected) {
			this.sendTrigger({
				category: 'Create Discussion',
				action: 'click_select_community_create',
				module: 'Forums / Communities',
				title: selected.category_name,
				id1: selected.thread_category_id,
			})
		},
		dwhViewForumEditPost() {
			this.sendTrigger({
				category: 'Edit Discussion',
				action: 'view_edit_discussion',
				module: 'Forums / Communities',
				title: this.$route.query.title,
				id1: this.$route.params.id,
			})
		},
		dwhViewForumPost() {
			this.sendTrigger({
				category: 'Create Discussion',
				action: 'view_create_discussion',
				module: 'Forums / Communities',
				title: 'Create Discussion',
			})
		},
		dwhCreateForumPost() {
			this.sendTrigger({
				category: 'Create Discussion',
				action: 'create_forum_post',
				module: 'Forums / Communities',
				title: this.fields.thread_title,
				id1: this.selectedCategory.thread_category_id,
			})
		},
		dwhEditForumPost() {
			this.sendTrigger({
				category: 'Edit Discussion',
				action: 'edit_forum_post',
				module: 'Forums / Communities',
				title: this.editableForum.thread_title,
				id1: this.editableForum.thread_id,
			})
		},
		markAsSaved() {
			this.initialSnapshot = this.getSnapshot()
		},
		getSnapshot() {
			return JSON.stringify({
				selectedCategory:
					typeof this.selectedCategory === 'object'
						? this.selectedCategory?.thread_category_id || null
						: this.selectedCategory || null,
				fields: this.fields,
				slider_images: this.slider_images.map((i) => i.upload_id || i.id),
				uploads: this.uploads.map((i) => i.upload_id),
			})
		},

		hasUnsavedChanges() {
			if (!this.initialSnapshot) return false

			return this.initialSnapshot !== this.getSnapshot()
		},
		removeImages(index) {
			this.slider_images.splice(index, 1)
			this.fields.image_upload_ids.splice(index, 1)
			if (this.currentIndex >= this.slider_images.length) {
				this.currentIndex = this.slider_images.length - 1
			}
			this.$refs.photoInput && (this.$refs.photoInput.value = '')
		},
		goToSlide(index) {
			this.currentIndex = index
		},
		triggerPhotoInput() {
			this.$refs.photoInput.click()
		},
		async handleFileUpload(event) {
			const fileInput = event.target
			const files = Array.from(fileInput.files || [])

			if (!files.length) return

			const availableSlots = 10 - this.slider_images.length

			if (availableSlots <= 0) {
				this.showAlert({
					message: 'You can upload up to 10 images.',
					type: 'error',
				})
				fileInput.value = ''
				return
			}

			const filesToUpload = files.slice(0, availableSlots)

			if (files.length > availableSlots) {
				this.showAlert({
					message: `You can upload only ${availableSlots} more image(s). Maximum is 10.`,
					type: 'error',
				})
			}

			for (const file of filesToUpload) {
				await this.uploadFile(file)

				const uploaded = this.postFile

				if (!uploaded?.upload_id) continue

				this.slider_images.push({
					...uploaded,
					id: uploaded.upload_id,
					image_url: uploaded.url || uploaded.image_url,
				})

				this.fields.image_upload_ids.push(uploaded.upload_id)
			}
			let count = this.fields.image_upload_ids.length
			this.dwhClickAddPhoto(count)

			this.currentIndex = this.slider_images.length - 1
			fileInput.value = ''
		},

		initForm() {
			if (this.categoryId) {
				this.fields.thread_category_id = this.categoryId
			}

			if (!this.edit || !this.editableForum) return

			this.selectedCategory = this.editableForum.category || {
				thread_category_id: this.editableForum.thread_category_id,
			}
			const uploads = JSON.parse(JSON.stringify(this.editableForum.uploads || []))
			const sliderImages = JSON.parse(JSON.stringify(this.editableForum.slider_images || []))
			this.slider_images = sliderImages.map((item, index) => ({
				...item,
				id: item.upload_id || index,
			}))

			this.fields = {
				thread_category_id: this.editableForum.thread_category_id,
				thread_text: this.editableForum.thread_text,
				upload_ids: uploads.map((item) => item.upload_id),
				thread_title: this.editableForum.thread_title,
				image_upload_ids: sliderImages.map((item) => item.upload_id),
			}
			this.uploads = uploads
		},

		onOpenCategorySelect() {
			this.$nextTick(() => {
				setTimeout(() => {
					this.attachScrollListener()
				}, 0)
			})
		},

		attachScrollListener() {
			const menu = document.querySelector('.post-text-category-menu')

			if (!menu) return

			menu.removeEventListener('scroll', this.handleScroll)
			menu.addEventListener('scroll', this.handleScroll)
		},

		removeScrollListener() {
			const menu = document.querySelector('.post-text-category-menu')

			if (!menu) return

			menu.removeEventListener('scroll', this.handleScroll)
		},

		async handleScroll(event) {
			const el = event.target
			const isBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 10

			if (!isBottom) return
			if (this.loadingMore) return
			if (this.page >= this.pageModalCategories) return

			this.loadingMore = true
			this.page += 1

			await this.getModalCategories({
				query: '?category_type=1&filter=bookmark&fields=thread_category_id,category_name',
				page: this.page,
			})

			this.loadingMore = false
		},

		onSelectChange(selectedValue) {
			this.fields.thread_category_id = selectedValue ? selectedValue.thread_category_id : null
			if (selectedValue) {
				this.dwhClickSelectCommunity(selectedValue)
			}
		},
		async onSubmit() {
			const isValid = this.$refs.form.validate()
			if (!this.selectedCategory) {
				this.showAlert({ message: 'Choose community.', type: 'error' })
				return
			}

			if (!isValid) return
			this.fields.thread_title = this.fields.thread_title?.trim()

			const plainText = this.fields.thread_text
				?.replace(/<[^>]*>/g, '')
				?.replace(/&nbsp;/g, ' ')
				?.trim()
			if (!this.fields.thread_title) {
				this.showAlert({ message: 'Title cannot be blank.', type: 'error' })
				return
			}

			if (!plainText) {
				this.showAlert({ message: 'Body cannot be blank.', type: 'error' })
				return
			}

			try {
				this.loading = true

				if (this.edit) {
					await this.editForum({
						id: this.editableForum.thread_id,
						data: this.fields,
					})
					this.dwhEditForumPost()
				} else {
					await this.addPost(this.fields)
					this.showAlert({ message: 'Post added.', type: 'success' })
					this.dwhCreateForumPost()
					this.$router.push({
						name: 'forum-by-id',
						params: { id: this.forumForRoute.thread_id },
					})
				}

				this.markAsSaved()
				this.closeModal()
			} catch (e) {
				if (e?.response?.status === 404 && this.edit) {
					this.$router.push({
						name: 'forums-by-category-id',
						params: { id: this.editableForum.thread_category_id },
					})
					this.showAlert({
						message: 'Unable to edit. This post has been deleted.',
						type: 'error',
					})
					return
				}
				this.showAlert({
					message:
						e?.response?.data?.[0]?.message ||
						'Something went wrong. Please try again.',
					type: 'error',
				})
			} finally {
				this.loading = false
			}
		},

		closeModal() {
			this.$emit('closeModal')
			if (!this.edit) {
				this.dwhClickCancelCreate()
			}
		},

		async handleUploadFile(event) {
			const files = event.target.files
			if (!files?.length) return

			for (const file of files) {
				await this.uploadFile(file)

				const uploaded = this.postFile

				if (!uploaded?.upload_id) continue

				this.fields.upload_ids.push(uploaded.upload_id)
				this.uploads.push(uploaded)
			}
			let count = this.fields.upload_ids.length
			this.dwhClickAddAttachment(count)

			event.target.value = null
		},
		triggerFileInput() {
			this.$refs.fileAttachmentInput.click()
		},
		removeFile(index) {
			this.uploads.splice(index, 1)
			this.fields.upload_ids.splice(index, 1)
		},
	},
	mounted() {
		if (this.edit) {
			this.dwhViewForumEditPost()
		} else {
			this.dwhViewForumPost()
		}
	},
}
</script>

<style lang="scss">
.post-text {
	&__wrapper {
		@media (max-width: 768px) {
			flex-direction: column;
		}
	}

	&__group-container {
		display: flex;
		justify-content: space-between;
		align-items: center;
		height: 48px;
		padding: 12px;
		border: 1px solid #e2e2e2;
		border-radius: 8px;

		&__title {
			flex-shrink: 0;
			font-family: Inter, sans-serif;
			font-weight: 500;
			font-size: 18px;
			line-height: 21.78px;
			color: #4a4a4a;
			&__input-title {
				font-family: Inter, sans-serif;
				font-weight: 400;
				font-size: 18px;
				line-height: 100%;
				letter-spacing: 0;
				outline: none;
				color: #4a4a4a;
				resize: none;
				overflow: hidden;
				&::placeholder {
					color: #9b9b9b;
				}
			}
			&__required {
				color: red;
			}
		}

		.v-text-field .v-input__prepend-inner,
		.v-text-field .v-input__append-inner {
			margin-top: 0;
		}
	}

	&__category-loader {
		width: 250px;
		height: 40px;
	}

	&__category-select-wrapper {
		width: 250px;
		min-width: 250px;
		max-width: 250px;
	}

	&__category-select {
		width: 100%;

		.v-input__control {
			width: 100%;
			min-height: 40px !important;
		}

		.v-input__slot {
			width: 100%;
			min-height: 40px !important;
			box-shadow: none !important;
			margin-bottom: 0;
			padding: 0 8px 0 0 !important;
		}

		.v-select__slot {
			display: flex !important;
			align-items: center !important;
			width: 100%;
			min-width: 0;
		}

		.v-select__selections {
			flex: 1;
			min-width: 0;
			flex-wrap: nowrap;
			overflow: hidden;
			justify-content: flex-end;
			padding-left: 8px;

			font-family: 'Inter', sans-serif;
			font-weight: 400;
			font-size: 18px;
			line-height: 120%;
			letter-spacing: 0;
			color: #4a4a4a !important;
		}

		.v-select__selection--comma {
			display: block;
			max-width: 300px;
			margin-left: 0;
			margin-right: 6px;
			overflow: hidden !important;
			text-overflow: ellipsis;
			white-space: nowrap;
			font-weight: 700 !important;
			text-align: right;
		}

		.v-select__slot input {
			width: 0 !important;
			min-width: 0 !important;
			max-width: 0 !important;
			padding: 0 !important;
		}

		.v-input__append-inner {
			flex-shrink: 0;
			margin-top: 0 !important;
			align-self: center;
		}

		.v-icon.v-icon {
			margin-left: 4px !important;
			color: #9b9b9b !important;
		}
	}
	&__body-contaner {
		display: flex;
		flex-direction: column;
		padding: 12px;
		gap: 8px;
		border: 1px solid #e2e2e2;
		border-radius: 8px;
	}

	&__btn-container {
		display: flex;
		justify-content: center;
		align-items: center;
		gap: 16px;
		padding-right: 28px;

		&__cancel {
			width: 150px;
			height: 46px;
			border: 1.5px solid var(--secondary_color) !important;
			border-radius: 8px;
			color: var(--secondary_color) !important;
			font-family: 'Inter', sans-serif;
			font-weight: 700;
			font-size: 18px;
			line-height: 100%;
			letter-spacing: 0;
			text-align: center;
		}

		&__submit {
			width: 150px;
			height: 46px;
			border-radius: 8px;
			background-color: var(--primary_color) !important;
			color: #ffffff;
			font-family: 'Inter', sans-serif;
			font-weight: 700;
			font-size: 18px;
			line-height: 100%;
			letter-spacing: 0;
			text-align: center;
		}
	}
}
</style>
